import React, {useRef} from 'react';
import styled from "styled-components";
import useAutoCollapse from "../../libs/hook/useAutoCollapse";
import IconArrowLeft from "../../../../shared/ui/icons/icon-arrow-left.ui";
import FilterRenderer from "./filter-renderer/FilterRenderer";
import {GLOBAL_STYLE_CONFIG} from "../../../../app/styles/GlobalStyles";
import {FilterSelectorsType, FilterStateType} from "../../model/types";
import {OnChangeFilterHandler} from "../../model/useFilterHandler";

const StyledProductFilter = styled.div`
    flex-wrap: wrap;
    gap: 8px;
    display: grid;
    grid-template-columns:100%;
    position: relative;
    align-items: flex-start;


    & > .${GLOBAL_STYLE_CONFIG.splitByPrefix('filter-collapse-btn')} {
        transform: rotate(90deg);
        transition: transform 250ms;
        margin: 0 auto;
        cursor: pointer;

    }

    &.${GLOBAL_STYLE_CONFIG.splitByPrefix('')}open {
        .${GLOBAL_STYLE_CONFIG.splitByPrefix('filter-collapse-btn')} {
            transform: rotate(270deg);
        }
    }

    @media (min-width: 768px) {
        grid-template-columns: 1fr 50px;
        .${GLOBAL_STYLE_CONFIG.splitByPrefix('filter-collapse-btn')} {
            margin: 10px 10px 0 auto;
        }
    }

`
const StyledContent = styled.div<{ collapseFrom?: number; expanded?: boolean }>`
    display: flex;
    flex-wrap: wrap;
    column-gap: 8px;
    row-gap: ${props => props.expanded ? '8px' : '0px'};
    align-items: center;
    ${({collapseFrom = 9999, expanded}) =>
            !expanded &&
            `
    > *:nth-child(n + ${collapseFrom + 1}) {
        visibility: hidden;
        height: 0px;
        padding: 0;
        border: none;
        font-size: 0px;
    }
  `}

`

/**
 * Компонент отображения фильтров для страницы товаров.
 *
 * Основные задачи:
 * - Отрисовка всех фильтров из конфигурации `filter.selectors`
 * - Использование универсального рендерера `FilterRenderer` для различных типов фильтров
 * - Обработка изменения фильтров и запуск поиска с обновлённым состоянием
 * - Скрытие части фильтров, если их слишком много (авто-сворачивание через `useAutoCollapse`)
 * - Сброс всех фильтров и строки поиска при отсутствии результатов
 * - Показ кнопки разворачивания/сворачивания фильтров
 *
 * Хук `useAutoCollapse` используется для управления видимостью фильтров в зависимости от количества.
 *
 * @returns JSX-элемент фильтров или блок `FilterNotResult`, если результатов по фильтру нет
 */

interface IProps {
    filtersSelector: FilterSelectorsType,
    filterState: FilterStateType,
    onChangeFilter: OnChangeFilterHandler,
}

const ProductFilters = ({filtersSelector, filterState, onChangeFilter}: IProps) => {
    const containerRef = useRef<HTMLElement>(null)

    const {
        visibleCount,
        shouldCollapse,
        expanded,
        setExpanded,
    } = useAutoCollapse({containerRef, filters: filtersSelector})

    return (
        <StyledProductFilter className={`
        ${GLOBAL_STYLE_CONFIG.splitByPrefix('filter-collapse')}
        ${expanded ? GLOBAL_STYLE_CONFIG.splitByPrefix('open') : ''}`}>
            <StyledContent collapseFrom={visibleCount} expanded={expanded}
                           className={GLOBAL_STYLE_CONFIG.splitByPrefix('filter-content')}
                           ref={containerRef as React.Ref<HTMLDivElement>}>

                {filtersSelector && Object.keys(filtersSelector).map((keyField: string) => {
                    const field = filtersSelector[keyField];
                    const filedState = filterState?.[keyField];
                    return (<FilterRenderer keyField={keyField} field={field} filedState={filedState}
                                            onChangeHandler={onChangeFilter}/>)
                })}
            </StyledContent>

            {shouldCollapse &&
                (<IconArrowLeft className={GLOBAL_STYLE_CONFIG.splitByPrefix('filter-collapse-btn')}
                                onClick={() => setExpanded(!expanded)}/>)}
        </StyledProductFilter>
    );
};

export default ProductFilters;